import { useState } from "react"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useRefreshSponsorPortal, useSponsorPortal } from "./sponsor-session-context"

type GivingSchedule = "monthly" | "termly" | "yearly"

export function DonorSettingsPage() {
  const portal = useSponsorPortal()
  const refresh = useRefreshSponsorPortal()
  const sponsor = portal?.sponsor
  const [name, setName] = useState(sponsor?.name ?? "")
  const [phone, setPhone] = useState(sponsor?.phone ?? "")
  const [city, setCity] = useState(sponsor?.city ?? "")
  const [schedule, setSchedule] = useState<GivingSchedule>((sponsor?.givingSchedule as GivingSchedule | undefined) ?? "monthly")
  const [emailUpdates, setEmailUpdates] = useState(sponsor?.emailUpdates ?? true)
  const [physicalLetters, setPhysicalLetters] = useState(sponsor?.physicalLetters ?? false)
  const [publicName, setPublicName] = useState(sponsor?.publicName ?? true)
  const [saving, setSaving] = useState(false)

  if (!sponsor) return null

  const save = async () => {
    setSaving(true)
    try {
      const response = await client.api.sponsor.portal.profile.$patch({
        json: {
          name,
          phone: phone || null,
          city: city || null,
          givingSchedule: schedule,
          emailUpdates,
          physicalLetters,
          publicName,
        },
      })
      const body = await response.json()
      if (!response.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      await refresh?.()
      toast.success("Changes saved")
    } catch {
      toast.error("Could not save your changes. Try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="donor-settings">
      <div className="donor-settings__header">
        <h1>Settings</h1>
        <p>Profile, giving cadence, and how we keep in touch.</p>
      </div>

      <div className="donor-settings__sections">
        <section className="donor-settings__section">
          <h2>Profile</h2>
          <div className="donor-settings__grid">
            <label className="donor-settings__field">
              <span>Full name</span>
              <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
            </label>
            <label className="donor-settings__field">
              <span>Email</span>
              <input type="email" value={sponsor.email} readOnly disabled />
            </label>
            <label className="donor-settings__field">
              <span>Phone</span>
              <input type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
            </label>
            <label className="donor-settings__field">
              <span>City</span>
              <input type="text" value={city} onChange={(event) => setCity(event.target.value)} />
            </label>
          </div>
        </section>

        <section className="donor-settings__section">
          <h2>Giving schedule</h2>
          <p className="donor-settings__section-desc">How often would you like your recurring contribution to run?</p>
          <div className="donor-settings__schedule">
            {(["monthly", "termly", "yearly"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setSchedule(option)}
                className={`donor-settings__schedule-btn${schedule === option ? " donor-settings__schedule-btn--active" : ""}`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </section>

        <section className="donor-settings__section">
          <h2>Communication</h2>
          <div className="donor-settings__toggles">
            <label className="donor-settings__toggle">
              <span>Email me term updates and report cards</span>
              <input type="checkbox" checked={emailUpdates} onChange={(event) => setEmailUpdates(event.target.checked)} />
              <span className="donor-settings__toggle-slider" />
            </label>
            <label className="donor-settings__toggle">
              <span>Send physical letters from my sponsored children</span>
              <input type="checkbox" checked={physicalLetters} onChange={(event) => setPhysicalLetters(event.target.checked)} />
              <span className="donor-settings__toggle-slider" />
            </label>
            <label className="donor-settings__toggle">
              <span>Show my name publicly as a supporter</span>
              <input type="checkbox" checked={publicName} onChange={(event) => setPublicName(event.target.checked)} />
              <span className="donor-settings__toggle-slider" />
            </label>
          </div>
        </section>

        <div className="donor-settings__actions">
          <button type="button" className="donor-settings__save" disabled={saving} onClick={() => void save()}>
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  )
}
